<template>
  <v-row dense>
    <v-col cols="12">
      <v-row>
        <v-col cols="6">
          <v-menu
            ref="menuFrom"
            v-model="menuFrom"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
            :readonly="readonly"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="form.from | dateFormat"
                readonly
                :disabled="form.editable_from"
                role="button"
                autocomplete="nope"
                append-icon="mdi-calendar"
                :label="$t('SUBSCRIPTIONS.DATE_FROM')"
                :error-messages="errors.from"
                :rules="getRulesByName('date_from')"
                v-on="on"
              />
            </template>
            <v-date-picker
              ref="picker"
              v-model="from"
              first-day-of-week="1"
              @change="saveDateFrom"
            />
          </v-menu>
        </v-col>
        <v-col cols="6">
          <v-menu
            ref="menuTo"
            v-model="menuTo"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
            :readonly="readonly"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="form.to | dateFormat"
                readonly
                clearable
                role="button"
                autocomplete="nope"
                append-icon="mdi-calendar"
                :label="$t('SUBSCRIPTIONS.DATE_TO')"
                :error-messages="errors.to"
                :rules="getRulesByName('date_to')"
                @click:clear="clearDateTo"
                v-on="on"
              />
            </template>
            <v-date-picker
              ref="picker"
              v-model="to"
              :min="minDateTo"
              first-day-of-week="1"
              @change="saveDateTo"
            />
          </v-menu>
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <v-textarea
        v-model="form.info"
        rows="2"
        outlined
        value="Additional info"
        :readonly="readonly"
        :label="$t('ITEM.ADDITIONAL_INFO')"
        :rules="getRulesByName('info')"
        counter="400"
        :error-messages="errors.info"
        @input="updateValue('info', $event)"
      />
    </v-col>

    <v-col cols="12">
      <v-switch
        v-model="form.include_in_rent"
        name="include_in_rent"
        :readonly="readonly"
        :label="$t('SUBSCRIPTIONS.INCLUDE_IN_RENT')"
        :error-messages="errors.include_in_rent"
        @change="updateIncludeInRent($event)"
      />
    </v-col>

    <v-col v-if="!form.include_in_rent" cols="12">
      <v-row dense>
        <v-col cols="6">
          <v-select
            v-model.number="form.billing_period"
            name="billing_period"
            item-text="name"
            item-value="id"
            :items="billingPeriods"
            :readonly="readonly"
            :label="`${$t('SUBSCRIPTIONS.BILLING_PERIOD')} *`"
            :rules="getRulesByName('billing_period')"
            :error-messages="errors.billing_period"
            clearable
            @change="updateValue('billing_period', $event)"
          />
        </v-col>
        <v-col cols="6">
          <money-input
            v-model="form.price_per_month"
            name="price_per_month"
            :label="`${$t('SUBSCRIPTIONS.PRICE_PER_MONTH')} *`"
            :readonly="readonly"
            :error-messages="errors.price_per_month"
            :rules="getRulesByName('price_per_month')"
            @input="updateValue('price_per_month', $event)"
          />
        </v-col>
      </v-row>
      <v-row v-if="isEdit" dense>
        <v-col cols="12">
          <p class="text-body-1">
            <span class="text--secondary">
              {{ $t('SUBSCRIPTIONS.START_BILLING_PERIOD') }}:
            </span>
            <span class="text--primary">
              {{ form.start_billing | dateFormat }}
            </span>
          </p>
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <v-autocomplete
        v-model="form.device_id"
        name="device_id"
        item-text="inline"
        item-value="id"
        :items="devices"
        :readonly="readonly"
        :label="$t('DEVICES.ITEM')"
        :rules="getRulesByName('device_id')"
        :error-messages="errors.device_id"
        clearable
        cache-items
        @change="updateValue('device_id', $event)"
      />
    </v-col>
  </v-row>
</template>

<script>
import moment from 'moment/moment';
import { dateFormat, dateFormatBackend } from '@/modules/_global/filters';
import {
  maxLength,
  maxLengthPriceNumber,
  notEmpty
} from '@/modules/_global/variables/rules.js';
import formEditable, { EDIT } from '@/modules/_global/mixins/form-editable';
import { numbersOnly } from '@/modules/_global/utils/events';
import { HALF_YEAR } from '@/modules/subscriptions/common/billing-periods';
import { SubscriptionsService } from '@/services/subscriptions.service';
import { maxDaysAgoCancelNotruf } from '@/modules/_global/config';
import { DevicesService } from '@/services/devices.service';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';

export default {
  name: 'SubscriptionEditForm',
  components: { MoneyInput },

  filters: {
    dateFormat,
    dateFormatBackend
  },

  mixins: [formEditable],

  props: {
    personId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      form: {
        from: null,
        to: null,
        info: '',
        include_in_rent: true,
        billing_period: HALF_YEAR,
        price_per_month: 0,
        start_billing: null,
        device_id: null,
        editable_from: false
      },
      rules: {
        date_from: [notEmpty],
        info: [maxLength(400)],
        billing_period: [notEmpty],
        price_per_month: [notEmpty, maxLengthPriceNumber(6)]
      },
      menuFrom: false,
      menuTo: false,
      devices: [],
      billingPeriods: []
    };
  },

  computed: {
    isEdit() {
      return this.action === EDIT;
    },

    to: {
      get() {
        return dateFormatBackend(this.form.to);
      },
      set(v) {
        this.form.to = v;
      }
    },

    from: {
      get() {
        return dateFormatBackend(this.form.from);
      },
      set(v) {
        this.form.from = v;
      }
    },

    maxDateBefore() {
      return dateFormatBackend(
        moment().subtract(maxDaysAgoCancelNotruf + 1, 'days')
      );
    },

    compareDates() {
      return [
        this.form.from,
        this.maxDateBefore,
        this.form.start_billing
      ].filter(Boolean);
    },

    maxCompareDate() {
      return this.compareDates.map(dateFormatBackend).reduce(function(a, b) {
        return a > b ? a : b;
      });
    },

    minDateTo() {
      const maxDateAfter = moment(this.maxCompareDate).add(1, 'days');

      return dateFormatBackend(maxDateAfter);
    }
  },

  mounted() {
    SubscriptionsService.getBillingPeriods().then(({ data }) => {
      this.billingPeriods = data.data;
    });

    DevicesService.getFilteredList({
      person: this.personId
    }).then(({ data }) => {
      this.devices = data.data;
    });
  },

  methods: {
    clearDateTo() {
      this.form.to = null;
    },

    updateIncludeInRent(value) {
      this.form.include_in_rent = value;

      if (this.form.billing_period == null) {
        this.form.billing_period = HALF_YEAR;
      }

      if (this.form.price_per_month == null) {
        this.form.price_per_month = 0;
      }
    },

    saveDateTo(date) {
      this.$refs.menuTo.save(date);
      this.updateValue('to', date);
    },

    saveDateFrom(date) {
      this.$refs.menuFrom.save(date);
      this.updateValue('from', date);
    },

    numbersOnly
  }
};
</script>
